"use client";

import { useCallback, useState } from "react";

// Helper function to format bytes to human-readable format
export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / k ** i).toFixed(dm))}${sizes[i]}`;
};

export const createPreview = (
  file: File | FileMetadata,
): string | undefined => {
  if (file instanceof File) {
    return URL.createObjectURL(file);
  }

  return file.url;
};

export const generateUniqueId = (file: File | FileMetadata): string => {
  if (file instanceof File) {
    // Generate a deterministic id based on file name, size, and lastModified
    return `${file.name}-${file.size}-${file.lastModified}`;
  }

  return file.id;
};

export interface FileMetadata {
  name: string;
  size: number;
  type: string;
  url: string;
  id: string;
}

export interface FileWithPreview {
  file: File | FileMetadata;
  id: string;
  preview?: string;
}

export interface FileUploadOptions {
  maxFiles?: number; // Only used when multiple is true, defaults to Infinity
  maxSize?: number; // in bytes
  accept?: string;
  multiple?: boolean; // Defaults to false
  files?: FileWithPreview[];
  onFilesChange?: (files: FileWithPreview[]) => void; // Callback when files change
  onFilesAdded?: (addedFiles: FileWithPreview[], isEditing?: boolean) => void; // Callback when new files are added
}

// Drag-and-drop and the file input belong to FileDropArea; this hook only
// validates, previews and tracks the files it is handed.
export interface FileUploadState {
  errors: string[];
}

export interface FileUploadActions {
  addFiles: (files: FileList | File[], isEditing?: boolean) => void;
  removeFile: (id: string) => void;
  clearFiles: () => void;
  clearErrors: () => void;
}

export const useFileUpload = (
  options: FileUploadOptions = {},
): [FileUploadState, FileUploadActions] => {
  const {
    maxFiles = Number.POSITIVE_INFINITY,
    maxSize = Number.POSITIVE_INFINITY,
    accept = "*",
    multiple = false,
    files = [],
    onFilesChange,
    onFilesAdded,
  } = options;

  const [state, setState] = useState<FileUploadState>({
    errors: [],
  });

  const validateFile = useCallback(
    (file: File | FileMetadata): string | null => {
      if (file instanceof File) {
        if (file.size > maxSize) {
          return `File "${file.name}" exceeds the maximum size of ${formatBytes(maxSize)}.`;
        }
      } else {
        if (file.size > maxSize) {
          return `File "${file.name}" exceeds the maximum size of ${formatBytes(maxSize)}.`;
        }
      }

      if (accept !== "*") {
        const acceptedTypes = accept.split(",").map((type) => type.trim());
        const fileType = file instanceof File ? file.type || "" : file.type;
        const fileExtension = `.${file instanceof File ? file.name.split(".").pop() : file.name.split(".").pop()}`;

        const isAccepted = acceptedTypes.some((type) => {
          if (type.startsWith(".")) {
            return fileExtension.toLowerCase() === type.toLowerCase();
          }

          if (type.endsWith("/*")) {
            const baseType = type.split("/")[0];

            return fileType.startsWith(`${baseType}/`);
          }

          return fileType === type;
        });

        if (!isAccepted) {
          return `File "${file instanceof File ? file.name : file.name}" is not an accepted file type.`;
        }
      }

      return null;
    },
    [accept, maxSize],
  );

  const clearFiles = useCallback(() => {
    setState((prev) => {
      const newState = {
        ...prev,
        errors: [],
      };

      return newState;
    });

    onFilesChange?.([]);

    files.forEach((file) => {
      if (file.preview && file.file instanceof File) {
        // Clean up object URLs for all file types that use createObjectURL
        URL.revokeObjectURL(file.preview);
      }
    });
  }, [onFilesChange, files]);

  const addFiles = useCallback(
    (newFiles: FileList | File[], isEditing = false) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!newFiles || newFiles.length === 0) return;

      const newFilesArray = Array.from(newFiles);
      const errors: string[] = [];

      // Clear existing errors when new files are uploaded
      setState((prev) => ({ ...prev, errors: [] }));

      // In single file mode, clear existing files first
      if (!multiple) {
        clearFiles();
      }

      // Check if adding these files would exceed maxFiles (only in multiple mode)
      if (
        multiple &&
        maxFiles !== Number.POSITIVE_INFINITY &&
        files.length + newFilesArray.length > maxFiles
      ) {
        errors.push(`You can only upload a maximum of ${maxFiles} files.`);
        setState((prev) => ({ ...prev, errors }));

        return;
      }

      const validFiles: FileWithPreview[] = [];

      newFilesArray.forEach((file) => {
        // Only check for duplicates if multiple files are allowed
        if (multiple) {
          const isDuplicate = files.some(
            (existingFile) =>
              existingFile.file.name === file.name &&
              existingFile.file.size === file.size,
          );

          // Skip duplicate files silently
          if (isDuplicate) {
            return;
          }
        }

        // Check file size
        if (file.size > maxSize) {
          errors.push(
            multiple
              ? `Some files exceed the maximum size of ${formatBytes(maxSize)}.`
              : `File exceeds the maximum size of ${formatBytes(maxSize)}.`,
          );

          return;
        }

        const error = validateFile(file);

        if (error) {
          errors.push(error);
        } else {
          validFiles.push({
            file,
            id: generateUniqueId(file),
            preview: createPreview(file),
          });
        }
      });

      // Only update state if we have valid files to add
      if (validFiles.length > 0) {
        // Call the onFilesAdded callback with the newly added valid files
        onFilesAdded?.(validFiles, isEditing);
        const newFiles = !multiple ? validFiles : [...files, ...validFiles];

        onFilesChange?.(newFiles);

        setState((prev) => {
          return {
            ...prev,
            errors,
          };
        });
      } else if (errors.length > 0) {
        setState((prev) => ({
          ...prev,
          errors,
        }));
      }
    },
    [
      files,
      maxFiles,
      multiple,
      maxSize,
      validateFile,
      clearFiles,
      onFilesChange,
      onFilesAdded,
    ],
  );

  const removeFile = useCallback(
    (id: string) => {
      const fileToRemove = files.find((file) => file.id === id);

      if (fileToRemove?.preview && fileToRemove.file instanceof File) {
        URL.revokeObjectURL(fileToRemove.preview);
      }

      const newFiles = files.filter((file) => file.id !== id);
      onFilesChange?.(newFiles);
      setState((prev) => {
        return {
          ...prev,

          errors: [],
        };
      });
    },
    [onFilesChange, files],
  );

  const clearErrors = useCallback(() => {
    setState((prev) => ({
      ...prev,
      errors: [],
    }));
  }, []);

  return [
    state,
    {
      addFiles,
      removeFile,
      clearFiles,
      clearErrors,
    },
  ];
};

export const isImage = (file: FileWithPreview) => {
  if (file.file instanceof File) {
    return file.file.type.startsWith("image/");
  }

  // check if the file is a image by the extension
  return (
    file.file.name.endsWith(".jpg") ||
    file.file.name.endsWith(".jpeg") ||
    file.file.name.endsWith(".png") ||
    file.file.name.endsWith(".gif") ||
    file.file.name.endsWith(".bmp") ||
    file.file.name.endsWith(".webp")
  );
};
